import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { DossierGapEntry, DossierGaps as Gaps } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate } from '../lib/format';
import { BackLink, Card, Chip, Empty, Loading, PageHeader } from '../components/ui';

export function DossierGaps() {
  const { company } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['dossier-gaps', company?.id], queryFn: () => capi<Gaps>('/dossier/gaps') });

  return (
    <>
      <PageHeader crumb={t('dossierGapsCrumb')} title={t('dossierGaps')} sub={t('dossierGapsSub')} />
      <div className="stack">
        <BackLink to="/dossier" label={t('backToDossier')} />
        {!q.data ? <Loading /> : (
          <>
            <GapTable title={t('dossierGapsSent')} rows={q.data.sent} onOpen={(id) => navigate(`/invoices/${id}`)} />
            <GapTable title={t('dossierGapsReceived')} rows={q.data.received} onOpen={(id) => navigate(`/dossier/${id}`)} />
          </>
        )}
      </div>
    </>
  );
}

function GapTable({ title, rows, onOpen }: { title: string; rows: DossierGapEntry[]; onOpen: (id: string) => void }) {
  const { t, s, lang } = useI18n();
  return (
    <Card pad={false} className="table">
      <div className="row" style={{ padding: '0.9rem 1rem 0' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div className="spacer" />
        <span className="small muted">{rows.length}</span>
      </div>
      <table>
        <thead><tr><th>{t('colIssued')}</th><th>{t('colSupplier')}</th><th>{t('invoiceNumber')}</th><th>{t('colBoekstuk')}</th><th>{t('colStatus')}</th></tr></thead>
        <tbody>
          {rows.length === 0 && <Empty />}
          {rows.map((r, i) => (
            <tr key={r.id ?? i} className={r.id ? 'click' : ''} onClick={() => r.id && onOpen(r.id)}>
              <td className="secondary small">{r.date ? formatDate(r.date, lang) : '—'}</td>
              <td style={{ fontWeight: 600 }}>
                {r.party || <span className="muted">{s('Unrecognised')}</span>}
                {!r.id && <span className="small muted" style={{ marginLeft: '0.4rem' }}>{t('dossierGapNotArchived')}</span>}
              </td>
              <td className="ref">{r.number ?? '—'}</td>
              <td className="secondary small mono">{r.ebEntryNumber ?? '—'}</td>
              <td>
                <div className="row" style={{ gap: '0.3rem' }}>
                  {r.missingPdf && <Chip tone="danger" icon="fas fa-file-circle-xmark">{t('dossierGapMissingPdf')}</Chip>}
                  {r.missingBooking && <Chip tone="warning" icon="fas fa-book">{t('dossierGapMissingBooking')}</Chip>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
